import { useSyncExternalStore } from "react";
import { isNativeApp } from "./platform";
export interface InstallPrompt extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}
let state: { prompt: InstallPrompt | null; installed: boolean } = {
  prompt: null,
  installed: false,
};
const listeners = new Set<() => void>();
const update = (next: typeof state) => {
  state = next;
  listeners.forEach((fn) => fn());
};
let initialized = false;
export function initializeInstallation() {
  if (initialized || isNativeApp()) return;
  initialized = true;
  const display = window.matchMedia("(display-mode: standalone)");
  const detect = () =>
    update({
      ...state,
      installed:
        display.matches ||
        (navigator as Navigator & { standalone?: boolean }).standalone === true,
    });
  detect();
  display.addEventListener?.("change", detect);
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    update({ ...state, prompt: event as InstallPrompt });
  });
  window.addEventListener("appinstalled", () =>
    update({ installed: true, prompt: null })
  );
  if (
    process.env.NODE_ENV === "production" &&
    "serviceWorker" in navigator &&
    window.isSecureContext
  ) {
    window.addEventListener("load", () => {
      navigator.serviceWorker.register("/sw.js").catch(() => {
        /* Installation remains available through the browser menu. */
      });
    });
  }
}
export function useAppInstallation() {
  const snapshot = useSyncExternalStore(
    (fn) => {
      listeners.add(fn);
      return () => {
        listeners.delete(fn);
      };
    },
    () => state
  );
  const install = async () => {
    const event = state.prompt;
    if (!event) return;
    update({ ...state, prompt: null });
    try {
      await event.prompt();
      return (await event.userChoice).outcome;
    } catch {
      return "error" as const;
    }
  };
  return { ...snapshot, install };
}
